<template>
  <div class="wrapper" id="collections-wrapper">
    <div class="" id="collections">
      <div class="card">
        <h3>Search all decks</h3>
        <input
          class="input w-full"
          type="text"
          placeholder="Enter a term..."
          v-model="searchTerm"
          @input="search"
        />
      </div>

      <div class="card" id="collections-overview">
        <!-- FAB -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          @click="newCollection"
          class="icon"
          id="new-card"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 id="collections-heading">My Decks</h3>
        <transition-group name="list" id="collections-list" tag="ul">
          <li
            v-for="collection in collections"
            :key="collection.id"
            class="collection"
            @click="this.activeCollection = collection"
            :class="{
              activeCollection: collection == this.activeCollection,
              disabled: collection.isDisabled,
            }"
          >
            {{ collection.emoji }}
            {{ collection.name }}
          </li>
        </transition-group>
      </div>
    </div>

    <transition
      name="fade"
      mode="out-in"
      v-if="activeCollection && !showSearchResults"
    >
      <div id="activeCollection-card" class="card" :key="activeCollection.id">
        <div id="top-row">
          <input
            class="input"
            v-model="activeCollection.name"
            @change="updateCollection"
          />
          <button
            id="emoji-trigger"
            @change="updateCollection"
            @click="picker.togglePicker($event)"
          >
            {{ activeCollection.emoji }}
          </button>
        </div>
        <textarea
          v-model="activeCollection.description"
          placeholder="Description"
          class="small-input"
          id="description"
          @change="updateCollection"
          rows="8"
        ></textarea>

        <div id="danger-zone-wrapper">
          <details id="danger-zone">
            <summary id="danger-zone-sum">Danger Zone</summary>
            <div>
              <button
                class="button small-button"
                id="delete-activeCollection"
                @click="deleteCollection"
              >
                Delete Collection
              </button>
            </div>
            <div>
              <input
                class="input"
                type="checkbox"
                name="isDisabled"
                v-model="activeCollection.isDisabled"
                @change="updateCollection"
                :key="activeCollection.id"
              />
              <label for="isDisabled">Deactive Collection</label>
            </div>
            <div v-if="this.$store.state.profile.is_admin">
              <input
                class="input"
                type="checkbox"
                v-model="activeCollection.public"
                @change="updateCollection"
                :key="activeCollection.id"
                name="isPublic"
              />
              <label for="isPublic">Make Public</label>
              <br />
              <button @click="copyToClipboard" class="button">Copy Link</button>

              <br />

              <label for="categories">Categories</label>

              <input
                class="input"
                type="text"
                v-model="activeCollection.categories"
                @change="updateCollection"
                name="categories"
              />
            </div>
          </details>
        </div>
        <div id="preview-cards-wrapper">
          <div>
            {{ activeCards.length
            }}<span v-if="activeCards.length === 1000">+</span> cards in this
            collection
          </div>
          <div id="preview-cards">
            <div
              class="mini-card"
              :class="{ firstCard: index === 0 }"
              v-for="(minicard, index) in activeCards.slice(0, 12)"
              :key="minicard.id"
              @click="jumpToCard(minicard)"
            >
              {{ minicard.front }}
            </div>
            <span
              class="small"
              id="rest-counter"
              v-if="activeCards.length > 12"
            >
              +{{ activeCards.length - 12 }}
            </span>
          </div>
        </div>
        <div id="edit-cards-wrapper">
          <button
            class="button button-primary"
            @click="openCollection(activeCollection.id)"
          >
            View Cards
          </button>
        </div>
      </div>
    </transition>

    <div v-else class="">
      <div v-if="searchResults.length === 0" class="single-center">
        <div v-if="showSearchLoader">searching...</div>
        <div v-else>select a deck or search for a card</div>
      </div>
      <div v-else>
        <h3>Search Results</h3>
        <code> {{ searchResults.length }} results for "{{ searchTerm }}" </code>
        <div class="flex-list">
          <button
            v-for="listCard in searchResults"
            :key="listCard._id"
            :class="listCard.type + ' card-list-card search-result'"
            @click="jumpToCard(listCard)"
          >
            <span v-if="listCard._front">
              {{ listCard._front.substring(0, 100) }}
            </span>
            <span v-else> Unnamed Card </span>

            <span v-if="listCard._front.length > 100">...</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uuid } from "vue-uuid";
import { EmojiButton } from "@joeattardi/emoji-button";
import { supabase } from "../supabase";

export default {
  name: "Dashboard",
  components: {},
  data() {
    return {
      activeCollection: null,
      picker: new EmojiButton(),
      activeCards: [],
      searchTerm: "",
      searchResults: [],
      showSearchResults: false,
      showSearchLoader: false,
    };
  },
  created() {
    this.$store.dispatch("getCollections");

    this.picker.on("emoji", (selection) => {
      // handle the selected emoji here
      this.activeCollection["emoji"] = selection.emoji;
      this.updateCollection();
    });
  },
  mounted() {},
  methods: {
    search: async function () {
      // use "global_search" rpc with id_of_user and search_term
      try {
        this.showSearchLoader = true;
        // the resulting data will have _id instead of id and _front instead of front, convert it to the right format
        console.log("searching for", this.searchTerm);
        const { data, error } = await supabase.rpc("global_search", {
          id_of_user: supabase.auth.currentUser.id,
          search_term: this.searchTerm,
        });
        if (error) throw error;
        console.log("search result data", data);
        this.searchResults = data;
        this.showSearchLoader = false;
      } catch (error) {
        console.log("error", error);
      }
    },
    copyToClipboard: function () {
      window.prompt(
        "Copy to clipboard: Ctrl+C, Enter",
        `https://kaado.io/preview/${this.activeCollection.id}`
      );
    },
    jumpToCard: function (card) {
      card.pack_id = card._pack_id;
      card.id = card._id;
      this.$store.commit("setCurrentPackId", card.pack_id);
      this.$store.commit("setCurrentEditCardId", card.id);
      this.$router.push("/collection");
    },
    openCollection(id) {
      // set the current activeCollection id in the store
      this.$store.commit("setCurrentPackId", id);
      // navigate to the activeCollection page
      this.$router.push("/collection");
    },
    newCollection() {
      const id = uuid.v4();
      const newCollection = {
        id: id,
        name: "New Collection",
        description: null,
        userId: supabase.auth.currentUser.id,
        public: false,
        emoji: "📔",
      };
      this.$store.dispatch("addCollection", newCollection).then(() => {
        this.$store.dispatch("getCollections").then(() => {
          // set the activeCollection to the new collection by finding its index in this.collections by id
          this.activeCollection = newCollection;
        });
      });
    },
    deleteCollection: function () {
      if (
        confirm(
          "Do you REALLY want to delete the collection and all its cards?"
        ) === true
      ) {
        this.$store
          .dispatch("deleteCollection", this.activeCollection)
          .then(() => {
            this.$store.dispatch("getCollections");
            this.activeCollection = null;
          });
      }
    },
    updateCollection: function () {
      this.$store.dispatch("updateCollection", this.activeCollection);
    },
    cloneCollection(activeCollection) {
      this.$store.dispatch("cloneCollection", activeCollection);
    },
    async getCardsOfCollection(id) {
      try {
        const { error, data } = await supabase
          .from("cards")
          .select("*")
          .match({ pack_id: id });
        if (error) throw error;
        this.activeCards = data;
      } catch (error) {
        console.warn(error.error_description || error.message);
      }
    },
  },
  computed: {
    collections: function () {
      let collections = this.$store.state.collections;
      collections = collections.sort((a, b) => {
        if (a.isDisabled === b.isDisabled) {
          return a.name.localeCompare(b.name);
        } else if (a.isDisabled) {
          return 1;
        } else {
          return -1;
        }
      });

      return collections;
    },
  },
  watch: {
    activeCollection: function (newVal, oldVal) {
      if (newVal) {
        this.getCardsOfCollection(newVal.id);
        this.showSearchResults = false;
        this.showSearchLoader = false;
      }
    },
  },
};
</script>
